import { z } from "zod";
import { breaker, type Breaker } from "../breaker/breaker.js";
import { type FormFactor } from "../breaker/form-factor.js";
import type { Pole } from "../breaker/pole.js";
import type {
  ProtectionType,
  TestableProtection,
  TestResult,
  TestRecord,
} from "../breaker/protection.js";
import { CodecError } from "./envelope.js";

/**
 * Phase 6a codec for `Breaker` and its sub-types. Encode/decode for the
 * `Pole` / `FormFactor` / `ProtectionType` / `TestRecord` value types
 * lives here too — they're small enough that keeping them adjacent
 * beats spreading them across half a dozen files.
 *
 * The encoders build output objects in the canonical key order
 * (`formFactor`, `pole`, `protectionTypes`, optional fields
 * in declaration order) so `JSON.stringify` produces a byte-stable
 * shape. Undefined optional fields are omitted; arrays preserve domain
 * order; the smart factory (`breaker`) re-canonicalizes
 * `protectionTypes` on read.
 */

const quadConfigSchema = z.enum([
  "two-double",
  "double-plus-two-single",
  "four-single",
]);

const poleSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("single") }),
  z.object({ kind: z.literal("double") }),
  z.object({ kind: z.literal("triple") }),
  z.object({ kind: z.literal("tandem") }),
  z.object({ kind: z.literal("quad"), config: quadConfigSchema }),
]);

const formFactorSchema = z.enum(["full", "half"]);

const protectionTypeSchema = z.enum([
  "GFCI",
  "AFCI",
  "DFCI",
  "HACR",
  "ShuntTrip",
  "SWD",
  "HID",
]);

const testableProtectionSchema = z.enum(["GFCI", "AFCI", "DFCI"]);

const testResultSchema = z.enum(["pass", "fail"]);

const testRecordSchema = z.object({
  date: z.string().min(1),
  kind: testableProtectionSchema,
  result: testResultSchema,
  note: z.string().optional(),
});

export const breakerJsonSchema = z.object({
  formFactor: formFactorSchema,
  pole: poleSchema,
  protectionTypes: z.array(protectionTypeSchema),
  manufacturer: z.string().optional(),
  partNumber: z.string().optional(),
  aicRating: z.number().int().positive().optional(),
  installDate: z.string().min(1).optional(),
  testHistory: z.array(testRecordSchema),
});

export type BreakerJson = z.infer<typeof breakerJsonSchema>;

export function encodePole(p: Pole): Record<string, unknown> {
  if (p.kind === "quad") {
    return { kind: p.kind, config: p.config };
  }
  return { kind: p.kind };
}

export function decodePole(j: z.infer<typeof poleSchema>): Pole {
  switch (j.kind) {
    case "single":
    case "double":
    case "triple":
    case "tandem":
      return { kind: j.kind };
    case "quad":
      return { kind: j.kind, config: j.config };
  }
}

function encodeTestRecord(r: TestRecord): Record<string, unknown> {
  const out: Record<string, unknown> = {
    date: r.date,
    kind: r.kind satisfies TestableProtection,
    result: r.result satisfies TestResult,
  };
  if (r.note !== undefined) out["note"] = r.note;
  return out;
}

function decodeTestRecord(j: z.infer<typeof testRecordSchema>): TestRecord {
  return {
    date: j.date,
    kind: j.kind,
    result: j.result,
    note: j.note,
  };
}

export function encodeBreaker(b: Breaker): Record<string, unknown> {
  const out: Record<string, unknown> = {
    formFactor: b.formFactor satisfies FormFactor,
    pole: encodePole(b.pole),
    protectionTypes: b.protectionTypes.map((p) => p satisfies ProtectionType),
  };
  if (b.manufacturer !== undefined) out["manufacturer"] = b.manufacturer;
  if (b.partNumber !== undefined) out["partNumber"] = b.partNumber;
  if (b.aicRating !== undefined) out["aicRating"] = b.aicRating;
  if (b.installDate !== undefined) out["installDate"] = b.installDate;
  out["testHistory"] = b.testHistory.map(encodeTestRecord);
  return out;
}

export function decodeBreaker(input: unknown): Breaker {
  const parsed = breakerJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeBreaker: ${parsed.error.message}`,
    );
  }
  const j = parsed.data;
  return breaker({
    formFactor: j.formFactor,
    pole: decodePole(j.pole),
    protectionTypes: j.protectionTypes,
    ...(j.manufacturer !== undefined ? { manufacturer: j.manufacturer } : {}),
    ...(j.partNumber !== undefined ? { partNumber: j.partNumber } : {}),
    ...(j.aicRating !== undefined ? { aicRating: j.aicRating } : {}),
    ...(j.installDate !== undefined ? { installDate: j.installDate } : {}),
    testHistory: j.testHistory.map(decodeTestRecord),
  });
}
